import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { normalizeCity } from '@/lib/city/normalizer';

interface CityLinkProps {
  city: string;
  showIcon?: boolean;
  className?: string;
  iconClassName?: string;
  asChild?: boolean; // Render as a div instead of button
}

const CityLink: React.FC<CityLinkProps> = ({
  city,
  showIcon = true,
  className,
  iconClassName,
  asChild = false,
}) => {
  const navigate = useNavigate();

  // 1️⃣ Normalize the city name (lowercase, strip accents)
  const canonical = normalizeCity(city);

  // 2️⃣ Handler sends you to the facilities page with normalized state
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/dashboard/facilities', {
      state: { selectedCity: canonical }
    });
  };

  const sharedProps = {
    onClick: handleClick,
    className: cn(
      "flex items-center text-dark-800 hover:text-gold-400 transition-colors",
      className
    )
  };

  // 3️⃣ Capitalize first letter for display
  const displayName = canonical.charAt(0).toUpperCase() + canonical.slice(1);

  const content = (
    <>
      {showIcon && (
        <MapPin
          className={cn("h-4 w-4 mr-1 flex-shrink-0", iconClassName)}
        />
      )}
      <span className="truncate">{displayName}</span>
    </>
  );

  // Render as <div> if asChild, otherwise <button>
  return asChild ? (
    <div {...sharedProps}>{content}</div>
  ) : (
    <button {...sharedProps}>{content}</button>
  );
};

export default CityLink;
